import React, { useEffect } from 'react';
import Layout from 'components/layout';
import HeadData from 'components/head-data';
import ListingPage from 'components/dynamic-page/listing-page';
import listingPageProps, { DynamicListingPageServerSideProps, isErrorProps } from 'components/dynamic-page/listing-page/get-server-side-props';
import OffMarketPage from 'components/dynamic-page/off-market-page';
import createListing from 'data/listing';
import { createGetServerSideProps } from 'utils/next/shared-getServerSideProps';
import ErrorState from 'components/error-state';
import { AbortReasons } from 'constants/abort-reasons';
import { trackEvent } from 'utils/google-tag-manager';
import { GTM_LISTING_PAGE_LOAD_UI_ERROR, GTM_LISTING_PAGE_TIMEOUT_UI_ERROR } from 'constants/events';
import { CACHE_CONTROL_HEADER_NAME, CACHE_CONTROL_HEADER_VALUE } from 'constants/headers';
import { LISTING_PAGE } from 'constants/errorMessages';
import { LISTING_ROUTE } from 'components/dynamic-page/route-matchers';
import { FeaturesType } from 'contexts/features';
import { TenantTypes } from '@tenants/manager';
import Address from 'data/addresses';

import type { GetServerSidePropsContext } from 'next';
import type { HeadDataType } from 'components/head-data';

function trackGtmListingPageError(reason?: string) {
  if (!reason) return;
  switch (reason) {
    case AbortReasons.TIMEOUT:
      trackEvent(GTM_LISTING_PAGE_TIMEOUT_UI_ERROR);
      break;
    case AbortReasons.MANUAL:
    case AbortReasons.ON_UNMOUNT:
      break;
    default:
      console.warn(`Cannot load listing page due to unexpected reason: ${reason}`);
      trackEvent(GTM_LISTING_PAGE_LOAD_UI_ERROR);
  }
}

export const getServerSideProps = createGetServerSideProps<DynamicListingPageServerSideProps>(async (context: GetServerSidePropsContext, features: FeaturesType, tenant: TenantTypes) => {
  context.res.setHeader(CACHE_CONTROL_HEADER_NAME, CACHE_CONTROL_HEADER_VALUE);
  const props = await listingPageProps(context, features, tenant);
  return { props };
});

export default function DynamicListingPage(props: DynamicListingPageServerSideProps) {

  useEffect(() => {
    // Run on client-side to call GTM without fail
    if (isErrorProps(props)) {
      const errorReason = props.props.errorReason;
      try {
        trackGtmListingPageError(errorReason);
      } catch (error) {
        console.error(error);
      }
    }
  }, [props]);

  if (isErrorProps(props)) {
    return (
      <Layout>
        <ErrorState
          image={{
            src: '/next/assets/images/not-found-error.svg',
            altText: 'Not Found Error',
          }}
          title={LISTING_PAGE.ERR_LISTING_NOT_FOUND}
          description={LISTING_PAGE.ERR_LISTING_NOT_FOUND_DESCRIPTION}
        />
      </Layout>
    );
  }

  const {
    headTags,
    isCrawler,
    footerData,
    addressesData,
    chartData,
    neighbourhoodData,
    zoopraisalData,
    demographics,
    breadcrumbs,
    tenantId,
    listingData,
    listingsData,
  } = props.props;
  const listing = new createListing(listingData);
  const listings = listingsData
    .map(listing => new createListing(listing))
    .sort((a, b) => a.addedAt < b.addedAt ? 1 : -1); //sort descending for price history component
  const addresses = addressesData.map(address => new Address(address));

  const offMarketHeadData: HeadDataType = {
    title: headTags?.title,
    schema: headTags?.schema,
    canonicalUrl: headTags?.canonicalUrl,
    ogUrl: headTags?.ogUrl,
    metaDescription: headTags?.metaDescription,
    hrefLangs: headTags?.hrefLangs,
  };

  return (
    <Layout showMobileBottomNav={false} routeName={LISTING_ROUTE}>
      {headTags && !listing.isOffMarket && <HeadData data={headTags} />}
      {listing.isOffMarket &&
        <HeadData data={offMarketHeadData} />}
      {listing.isOffMarket &&
        <OffMarketPage
          listing={listing}
          listings={listings}
          breadcrumbs={breadcrumbs}
          isCrawler={isCrawler} />}
      {!listing.isOffMarket &&
        <ListingPage
          tenantId={tenantId}
          listing={listing}
          listings={listings}
          breadcrumbs={breadcrumbs}
          isCrawler={isCrawler}
          footerData={footerData}
          addressesData={addresses}
          chartData={chartData}
          neighbourhoodData={neighbourhoodData}
          zoopraisalDataParams={zoopraisalData}
          demographics={demographics} />}
    </Layout>
  );
}
